import React, { useState } from 'react';
import {
  Box, Paper, Typography, Chip, Button, Stack,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReplayIcon from '@mui/icons-material/Replay';
import BlockIcon from '@mui/icons-material/Block';
import GavelIcon from '@mui/icons-material/Gavel';
import { useSnackbar } from 'notistack';
import { getLogsUrl } from '../utils/verificationAdmin';
import axios from '../auth/axiosConfig';

const STATUS_COLORS = {
  CREATED: 'default',
  PENDING: 'info',
  RUNNING: 'info',
  FAILED: 'error',
  NO_MATCH: 'warning',
};

const RunningVerificationPanel = ({ verificationId, vrDetailData, onRefresh }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [forceMatchOpen, setForceMatchOpen] = useState(false);
  const [stateId, setStateId] = useState('');
  const [jurisdictionCode, setJurisdictionCode] = useState('');
  const [confirm, setConfirm] = useState(null);

  const openForceMatch = () => {
    setStateId(vrDetailData._force_match_business_state_id || '');
    setJurisdictionCode(vrDetailData._force_match_business_jurisdiction_code || '');
    setForceMatchOpen(true);
  };

  const status = (vrDetailData._status || '').toUpperCase();
  const isFrozen = !!vrDetailData._is_frozen;
  const request = vrDetailData.request || {};
  const addr = request.address || {};
  const logsUrl = getLogsUrl(verificationId);
  const matchedEntity = vrDetailData._matched_entity;
  const matchedEntityIsStale = matchedEntity
    && status === 'PENDING'
    && !!vrDetailData._force_match_business_state_id;

  const createdAtDisplay = (() => {
    const raw = vrDetailData._created_at;
    if (!raw) return null;
    const hasTimezone = raw.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(raw);
    const date = new Date(hasTimezone ? raw : `${raw}Z`);
    if (Number.isNaN(date.getTime())) return raw;
    return date.toLocaleString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  })();

  const post = async (path, body, label) => {
    setLoading(true);
    try {
      const response = await axios.post(`/ui/${verificationId}/${path}`, body);
      enqueueSnackbar(response.data.message || `${label} successful`, { variant: 'success' });
      if (onRefresh) onRefresh();
    } catch (err) {
      enqueueSnackbar(err.response?.data?.detail || `Failed to ${label.toLowerCase()}`, { variant: 'error' });
    } finally {
      setLoading(false);
      setForceMatchOpen(false);
      setConfirm(null);
    }
  };

  const handleForceMatch = () => {
    const sid = stateId.trim();
    const jc = jurisdictionCode.trim().toLowerCase();
    if (!sid || !jc) {
      enqueueSnackbar('state_id and jurisdiction_code are required', { variant: 'error' });
      return;
    }
    // Defer format validation to the backend, which has the canonical
    // OPENCORPORATES_TO_US_STATE_CODES list and accepts non-US codes like
    // 'pr' and 'gb' that wouldn't match a simple us_xx regex.
    post('force-match', { state_id: sid, jurisdiction_code: jc }, 'Force match');
  };

  const formattedAddress = [addr.street_line_1, addr.street_line_2, addr.city, addr.state, addr.zip_code]
    .filter(Boolean).join(', ');

  const fields = [
    formattedAddress && { label: 'Address', value: formattedAddress },
    request.dba_name && { label: 'DBA', value: request.dba_name },
    request.ein && { label: 'EIN', value: request.ein },
    request.website && { label: 'Website', value: request.website },
    request.external_id && { label: 'External ID', value: request.external_id },
    request.applicant_name && { label: 'Applicant', value: request.applicant_name },
    request.applicant_emails?.length && { label: 'Applicant emails', value: request.applicant_emails.join(', ') },
    request.applicant_phone_numbers?.length && { label: 'Applicant phones', value: request.applicant_phone_numbers.join(', ') },
    request.applicant_date_of_birth && { label: 'Applicant DOB', value: request.applicant_date_of_birth },
    request.applicant_last_4_ssn && { label: 'Applicant SSN (last 4)', value: request.applicant_last_4_ssn },
    request.affiliated_people_names?.length && { label: 'Affiliated people', value: request.affiliated_people_names.join(', ') },
    { label: 'ID', value: vrDetailData._id || verificationId, mono: true },
    createdAtDisplay && { label: 'Created', value: createdAtDisplay },
  ].filter(Boolean);

  return (
    <Box sx={{ p: 3, backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
          <Typography variant="h6">{request.business_name || 'Unnamed business'}</Typography>
          <Chip label={status || 'UNKNOWN'} size="small" color={STATUS_COLORS[status] || 'default'} />
        </Stack>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, mt: 1 }}>
          {fields.map(({ label, value, mono }) => (
            <React.Fragment key={label}>
              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                {label}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontFamily: mono ? 'monospace' : undefined, wordBreak: 'break-word' }}
              >
                {value}
              </Typography>
            </React.Fragment>
          ))}
        </Box>
        {(vrDetailData._force_match_business_state_id || vrDetailData._force_match_business_jurisdiction_code) && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Current force match — state_id: <strong>{vrDetailData._force_match_business_state_id || '—'}</strong>,
            jurisdiction_code: <strong>{vrDetailData._force_match_business_jurisdiction_code || '—'}</strong>
          </Typography>
        )}
      </Paper>

      {matchedEntity && (
        <Paper elevation={2} sx={{ p: 3, mb: 3, opacity: matchedEntityIsStale ? 0.5 : 1 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>Matched OpenCorporates record</Typography>
          {matchedEntityIsStale && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontStyle: 'italic' }}>
              Previous match — re-processing with new force match
            </Typography>
          )}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Legal name</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>{matchedEntity.legal_name || '—'}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Jurisdiction</Typography>
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
              {matchedEntity.state_code || '—'} / {matchedEntity.state_id || '—'}
            </Typography>
            {matchedEntity.incorporation_date && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>Incorporated</Typography>
                <Typography variant="body2">
                  {new Date(matchedEntity.incorporation_date).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric',
                  })}
                </Typography>
              </>
            )}
          </Box>
          <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
            {matchedEntity.opencorporates_url && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon />}
                href={matchedEntity.opencorporates_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                OpenCorporates
              </Button>
            )}
            {matchedEntity.sos_url && (
              <Button
                variant="text"
                size="small"
                startIcon={<OpenInNewIcon />}
                href={matchedEntity.sos_url}
                target="_blank"
                rel="noopener noreferrer"
              >
                Secretary of State
              </Button>
            )}
          </Stack>
        </Paper>
      )}

      <Paper elevation={2} sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>Admin actions</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {isFrozen
            ? 'This verification is frozen. Unfreeze it before taking any action.'
            : "This verification hasn't produced a result yet. Use the actions below to nudge it."}
        </Typography>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <Button
            variant="contained"
            startIcon={<GavelIcon />}
            onClick={openForceMatch}
            disabled={loading || isFrozen}
          >
            Force match
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<BlockIcon />}
            onClick={() => setConfirm({
              action: 'mark-no-match',
              label: 'Mark NO_MATCH',
              description: 'Set this verification\'s status to NO_MATCH. It will not be reprocessed.',
            })}
            disabled={loading || isFrozen}
          >
            No match
          </Button>
          <Button
            variant="outlined"
            startIcon={<ReplayIcon />}
            onClick={() => setConfirm({
              action: 'restart',
              label: 'Restart',
              description: 'Set this verification back to PENDING so the processor picks it up again.',
            })}
            disabled={loading || isFrozen}
          >
            Restart
          </Button>
          <Button
            variant="text"
            startIcon={<OpenInNewIcon />}
            href={logsUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            Logs
          </Button>
        </Stack>
      </Paper>

      <Dialog open={forceMatchOpen} onClose={() => setForceMatchOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Force match</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Set the company state id and jurisdiction code. The verification will be
            re-queued (PENDING) for the processor.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="State id (company number)"
            value={stateId}
            onChange={(e) => setStateId(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Jurisdiction code"
            value={jurisdictionCode}
            onChange={(e) => setJurisdictionCode(e.target.value)}
            placeholder="e.g. us_ca"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setForceMatchOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={loading} onClick={handleForceMatch}>
            {loading ? 'Submitting…' : 'Force match'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirm} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{confirm?.label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{confirm?.description}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={loading || !confirm}
            onClick={() => confirm && post(confirm.action, undefined, confirm.label)}
          >
            {loading ? 'Processing…' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RunningVerificationPanel;
